import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Users, Search, UserPlus, UserMinus, Loader2, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export default function ManageTeam() {
  const { user } = useAuth();
  const [seniors, setSeniors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/team/seniors");
      setSeniors(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const assign = async (s) => {
    setBusy(s.id);
    try {
      await api.post("/team/assign", { senior_id: s.id });
      toast.success(`${s.full_name} added to your team`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (s) => {
    setBusy(s.id);
    try {
      await api.post("/team/remove", { senior_id: s.id });
      toast.success(`${s.full_name} removed from your team`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return seniors;
    const t = search.toLowerCase();
    return seniors.filter((s) => s.full_name.toLowerCase().includes(t) || s.email.toLowerCase().includes(t));
  }, [seniors, search]);

  const myTeam = filtered.filter((s) => s.on_my_team);
  const others = filtered.filter((s) => !s.on_my_team);

  if (user && user.role !== "manager") return <Navigate to="/" replace />;

  const Row = ({ s }) => (
    <div
      data-testid={`team-row-${s.id}`}
      className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 last:border-0"
    >
      <div className="flex min-w-0 items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-slate-800 text-sm font-bold text-white">
          {s.full_name.split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="truncate text-base font-semibold text-slate-800">{s.full_name}</p>
          <p className="truncate text-sm text-slate-500">{s.email}</p>
        </div>
      </div>
      {s.on_my_team ? (
        <Button data-testid={`team-remove-${s.id}`} variant="outline" disabled={busy === s.id}
          className="h-10 gap-2 border-rose-200 px-4 text-rose-600 hover:bg-rose-50" onClick={() => remove(s)}>
          {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />} Remove
        </Button>
      ) : s.assigned_elsewhere ? (
        <span data-testid={`team-elsewhere-${s.id}`} className="rounded-full bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-500">
          On another team
        </span>
      ) : (
        <Button data-testid={`team-add-${s.id}`} disabled={busy === s.id}
          className="h-10 gap-2 bg-blue-600 px-4 hover:bg-blue-700" onClick={() => assign(s)}>
          {busy === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Add to team
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNav />
      <main className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <div>
          <h1 className="font-heading flex items-center gap-2 text-3xl font-extrabold tracking-tight text-slate-900">
            <Users className="h-8 w-8 text-blue-600" /> Manage Team
          </h1>
          <p className="mt-1 text-lg text-slate-500">Assign Senior Merchandisers to your team to see their orders</p>
        </div>

        <div className="relative mt-6">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="team-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search senior merchandisers by name or email…"
            className="h-12 w-full rounded-lg border border-slate-200 bg-white pl-11 pr-4 text-base text-slate-800 outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <section className="mt-6">
              <h2 className="mb-2 flex items-center gap-2 text-lg font-bold text-slate-700">
                <Check className="h-5 w-5 text-emerald-500" /> My Team ({myTeam.length})
              </h2>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {myTeam.length === 0 ? (
                  <p className="px-5 py-6 text-base text-slate-400">No senior merchandisers on your team yet. Add them from the list below.</p>
                ) : (
                  myTeam.map((s) => <Row key={s.id} s={s} />)
                )}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="mb-2 text-lg font-bold text-slate-700">Available Senior Merchandisers ({others.length})</h2>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                {others.length === 0 ? (
                  <p className="px-5 py-6 text-base text-slate-400">No other senior merchandisers found in your factory.</p>
                ) : (
                  others.map((s) => <Row key={s.id} s={s} />)
                )}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
